import Head from 'next/head';
import Header from '../../components/Header';
import BlogPostCard from '../../components/BlogPostCard';
import Sidebar from '../../components/Sidebar';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import Hero from '../../components/Hero';
import { useRouter } from 'next/router';

const POSTS_PER_PAGE = 6;

export async function getStaticProps() {
  const fs = require('fs');
  const path = require('path');

  const blogDir = path.join(process.cwd(), 'pages/blog');
  const files = fs.readdirSync(blogDir);

  const posts = await Promise.all(
    files
      .filter(f =>
        f.endsWith('.js') &&
        f !== 'index.js' &&
        f !== '[page].js' &&
        !f.includes('[') &&
        !f.includes(']')
      )
      .map(async (file) => {
        try {
          const mod = await import(`./${file}`);
          const metadata = mod.metadata;

          if (!metadata) return null;

          return {
            ...metadata,
            link: `/blog/${file.replace('.js', '')}`,
          };
        } catch (e) {
          return null;
        }
      })
  );

  const allPosts = posts
    .filter(Boolean)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  return {
    props: {
      allPosts,
    },
  };
}

export default function BlogIndex({ allPosts }) {
  const router = useRouter();

  const search = router.query.q || '';
  const category = router.query.category || '';
  const tag = router.query.tag || '';

  const filtered = allPosts.filter((post) => {
    const matchSearch =
      !search ||
      post.title.toLowerCase().includes(search.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(search.toLowerCase());

    const matchCategory = !category || post.category === category;
    const matchTag = !tag || post.tags?.includes(tag);

    return matchSearch && matchCategory && matchTag;
  });

  const totalPages = Math.ceil(filtered.length / POSTS_PER_PAGE);

  const posts = filtered.slice(0, POSTS_PER_PAGE);

  const categories = [...new Set(allPosts.map(p => p.category).filter(Boolean))];
  const tags = [...new Set(allPosts.flatMap(p => p.tags || []).filter(Boolean))];

  const recentPosts = allPosts.slice(0, 3);

  const page = 1;

  return (
    <>
      <Head>
        <title>Home Remodeling Tips & Trends | Modern Build Home Remodeling</title>

        <meta name="robots" content="index, follow" />
        <meta name="title" content="Home Remodeling Tips & Trends | Modern Build Home Remodeling" />
        <meta name="description" content="Discover expert advice on remodeling, design trends, energy-efficient upgrades, and renovation ideas—fresh insights from Modern Build." />
        <meta name="author" content="Modern Build Home Remodeling" />
        <meta name="keywords" content="home remodeling, bathroom remodeling, kitchen renovation, adu, garage conversion, trends, tips, home improvement" />

        <link rel="canonical" href="https://modernbuildremodel.com/blog" />

        {/* Open Graph */}
        <meta property="og:title" content="Home Remodeling Tips & Trends | Modern Build Home Remodeling" />
        <meta property="og:description" content="Get inspired by remodeling tips, design trends, and renovation guides from Modern Build." />
        <meta property="og:image" content="https://modernbuildremodel.com/hero/los-angeles-california-best-home-remodling-contractor.webp" />
        <meta property="og:url" content="https://modernbuildremodel.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Home Remodeling Tips & Trends | Modern Build Home Remodeling" />
        <meta name="twitter:description" content="Expert remodeling advice, design inspiration, and renovation tips from Modern Build." />
        <meta name="twitter:image" content="https://modernbuildremodel.com/hero/los-angeles-california-best-home-remodling-contractor.webp" />
        <meta name="twitter:site" content="@modernbuildhome" />

        {/* BLOG SCHEMA */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "name": "Modern Build Blog",
              "url": "https://modernbuildremodel.com/blog",
              "description": "Home remodeling tips, guides, and design trends from Modern Build.",
              "mainEntityOfPage": "https://modernbuildremodel.com/blog",
              "publisher": {
                "@type": "Organization",
                "name": "Modern Build Home Remodeling",
                "url": "https://modernbuildremodel.com"
              }
            })
          }}
        />

        {/* WEBSITE SEARCH SCHEMA */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebSite",
              "name": "Modern Build Blog",
              "url": "https://modernbuildremodel.com/blog",
              "potentialAction": {
                "@type": "SearchAction",
                "target": "https://modernbuildremodel.com/blog?q={search_term_string}",
                "query-input": "required name=search_term_string"
              }
            })
          }}
        />

        {/*BreadcrumbList SCHEMA*/}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://modernbuildremodel.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": "https://modernbuildremodel.com/blog"
                }
              ]
            })
          }}
        />
      </Head>

      <Header />
      <Hero category="blog" />

      <section className="bg-white py-16">
        <div className="container mx-auto grid md:grid-cols-3 gap-12">

          {/* POSTS */}
          <div className="md:col-span-2 space-y-8">
            {posts.map((post, i) => (
              <BlogPostCard key={i} post={post} />
            ))}

{/* PAGINATION */}
{!search && !category && !tag && totalPages > 1 && (
  <div className="mt-10 flex items-center justify-center">
    <div className="flex items-center gap-2 bg-white p-2">

      {/* PREV (disabled on page 1) */}
      <span
        className="px-4 py-2 rounded-lg border bg-gray-100 text-gray cursor-not-allowed border-gray"
      >
        Prev
      </span>

      {/* PAGE 1 (active) */}
      <a
        href="/blog"
        className="px-4 py-2 rounded-lg border bg-primary text-white border-primary"
      >
        1
      </a>

      {/* PAGE LINKS */}
      {[...Array(totalPages - 1)].map((_, i) => {
        const p = i + 2;
        const href = `/blog/page/${p}`;

        return (
          <a
            key={p}
            href={href}
            className="px-4 py-2 rounded-lg border bg-white text-black border-black hover:bg-primary hover:text-white hover:border-primary  transition"
          >
            {p}
          </a>
        );
      })}

      {/* NEXT */}
      <a
        href="/blog/page/2"
        className="px-4 py-2 rounded-lg border bg-white text-black border-black hover:bg-primary hover:text-white hover:border-primary transition"
      >
        Next
      </a>

    </div>
  </div>
)}
          </div>

          {/* SIDEBAR */}
          <Sidebar
            allPosts={allPosts}
          />
        </div>
      </section>

      <SocialProof />
      <ContactUs />
      <Footer />
    </>
  );
}